import React, {useEffect, useState} from 'react';
import {useSnackbar} from "../../../contexts/SnackbarContext";
import {useParams} from "react-router-dom";
import DigitizationDetailsToolbar from "./components/DigitizationDetailsToolbar";
import DigitizationDetailsSidebar from "./components/DigitizationDetailsSidebar";

import {Box, CircularProgress, Stack, Tab, Tabs, Typography} from "@mui/material";

import FilePreviewResult from "./components/FilePreviewResult";
import {useDigitizationDetails} from "../../../hooks/useDigitizationDetails";
import {useTranslation} from "react-i18next";
import {
    StyledDigitizationCard,
    StyledDigitizationContent,
    StyledDigitizationPage,
    StyledFilePreviewWrapper
} from "./DigitizationDetails.styled";
import FilePreviewOriginal from "./components/FilePreviewOriginal";

interface TabPanelProps {
    children: React.ReactNode;
    value: number;
    index: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => (
    <Box
        hidden={value !== index}
        mt={2}
        sx={{
            display: value !== index ? 'none' : 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
        }}
    >
        {value === index && children}
    </Box>
);

const DigitizationDetails = () => {
    const {id} = useParams<{ id: string }>();
    const documentId = id ? parseInt(id) : undefined;
    const [activeTab, setActiveTab] = useState(0);

    const {t} = useTranslation();

    const {
        contentData,
        navigationData,
        historyData,
        originalBaseContent,
        ocrResult,
        isLoading,
        error,
        navigateToPreviousDocument,
        navigateToNextDocument,
        navigateBackToList,
        navigateToEdition,
        cancelDocument,
        confirmDocument,
        changeDocumentCategory,
        exportDocument,
    } = useDigitizationDetails(documentId);

    const {showSnackbar} = useSnackbar();

    useEffect(() => {
        if (error) {
            showSnackbar(t('digitizationDetails.messages.fetchDataError'), 'error');
        }
    }, [error, showSnackbar, t]);

    const handleChange = (_: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    const handleExport = async () => {
        const completed = await exportDocument();
        if (completed) {
            showSnackbar(t('digitizationDetails.messages.exportSuccess'), 'success');
        } else {
            showSnackbar(t('digitizationDetails.messages.exportError'), 'error');
        }
    }

    if (documentId === undefined) {
        return (
            <StyledDigitizationPage>
                <StyledDigitizationCard>
                    {t('digitizationDetails.messages.noDataFoundInfo')}
                </StyledDigitizationCard>
            </StyledDigitizationPage>
        );
    }

    return (
        <StyledDigitizationPage>
            <StyledDigitizationCard>
                {isLoading && (
                    <Stack justifyContent="center" alignItems="center" height="100%">
                        <CircularProgress size={50} />
                    </Stack>
                )}

                {!isLoading && (
                    <DigitizationDetailsToolbar
                        document={contentData}
                        navigation={navigationData}
                        onConfirmDocument={confirmDocument}
                        onCancelDocument={cancelDocument}
                        navigateToPreviousDocument={navigateToPreviousDocument}
                        navigateToNextDocument={navigateToNextDocument}
                        navigateBack={navigateBackToList}
                        navigateToEdition={navigateToEdition}
                        onExport={handleExport}
                    />
                )}

                {(!isLoading && (!contentData || !navigationData)) && (
                    <Stack justifyContent="center" alignItems="center" height="100%">
                        <Typography variant="h6">
                            {t('digitizationDetails.messages.noResultsInfo')}
                        </Typography>
                    </Stack>
                )}

                {(!isLoading && contentData && navigationData) && (
                    <StyledDigitizationContent>
                        <StyledFilePreviewWrapper>
                            <Box sx={{ marginBottom: 1 }}>
                                <Tabs value={activeTab} onChange={handleChange}>
                                    <Tab label={t('digitizationDetails.filePreview.originalTab')}/>
                                    <Tab label={t('digitizationDetails.filePreview.ocrResultTab')}/>
                                </Tabs>
                            </Box>

                            <TabPanel value={activeTab} index={0}>
                                {(!originalBaseContent)
                                    ? (
                                        <Typography variant="body1" fontWeight={500} color={'black'}>
                                            {t('digitizationDetails.filePreview.originalFileNotFound')}
                                        </Typography>
                                    )
                                    : (
                                        <FilePreviewOriginal
                                            fileName={contentData.fileName}
                                            contentType={contentData.contentType}
                                            baseContent={originalBaseContent}
                                        />
                                    )
                                }
                            </TabPanel>

                            <TabPanel value={activeTab} index={1}>
                                {!ocrResult
                                    ? (
                                        <Typography variant="body1" fontWeight={500} color={'black'}>
                                            {t('digitizationDetails.filePreview.ocrResultNotFound')}
                                        </Typography>
                                    )
                                    : (<FilePreviewResult result={ocrResult} />)
                                }
                            </TabPanel>
                        </StyledFilePreviewWrapper>

                        <DigitizationDetailsSidebar
                            document={contentData}
                            history={historyData}
                            onDocumentCategoryChange={changeDocumentCategory}
                        />
                    </StyledDigitizationContent>
                )}
            </StyledDigitizationCard>
        </StyledDigitizationPage>
    );
};

export default DigitizationDetails;